import { permission } from '../decorators';

import OneTrust, { type ConsentGroup } from './OneTrust';

enum ScriptState {
  INITIAL = 'initial',
  LOADING = 'loading',
  LOADED = 'loaded',
  BLOCKED = 'blocked',
  ERROR = 'error',
}

export class ScriptLoader {
  private static count = 0;
  private state: ScriptState = ScriptState.INITIAL;
  private error: Error = null;
  private onLoadCallbacks: Set<() => void> = new Set();
  private onBlockCallbacks: Set<() => void> = new Set();
  private onErrorCallbacks: Set<(error: Error) => void> = new Set();

  constructor(
    private readonly src: string,
    private readonly consentGroup: ConsentGroup,
    private readonly id: string = `script-${ScriptLoader.count++}`,
  ) {}

  public get isLoaded() {
    return this.state === ScriptState.LOADED;
  }

  public get isBlocked() {
    return this.state === ScriptState.BLOCKED;
  }

  @permission(typeof window !== 'undefined')
  public load(callback?: () => void): void | (() => void) {
    if (this.state === ScriptState.ERROR) return;

    if (this.state === ScriptState.LOADING) {
      if (callback) {
        this.onLoadCallbacks.add(callback);
      }
      return;
    }

    if (this.state === ScriptState.LOADED) {
      callback?.();
      return;
    }

    if (!OneTrust.isCookieConsentGroupAllowed(this.consentGroup)) {
      this.state = ScriptState.BLOCKED;
    }

    if (this.state === ScriptState.BLOCKED) {
      for (const callback of this.onBlockCallbacks) {
        callback();
      }
      callback?.();
      return;
    }

    let script = document.getElementById(this.id) as HTMLScriptElement | null;
    if (script) {
      this.state = ScriptState.LOADED;
      for (const callback of this.onLoadCallbacks) {
        callback();
      }
      callback?.();
      return;
    }

    this.state = ScriptState.LOADING;
    script = document.createElement('script');
    script.src = this.src;
    script.id = this.id;
    script.onload = () => {
      this.state = ScriptState.LOADED;
      for (const callback of this.onLoadCallbacks) {
        callback();
      }
      callback?.();
    };
    script.onerror = () => {
      this.error = new Error(`Script with source '${this.src}' could not be loaded`);
      this.state = ScriptState.ERROR;
      for (const callback of this.onErrorCallbacks) {
        callback(this.error);
      }
    };

    document.head.appendChild(script);

    return;
  }

  public onLoad(callback: () => void): () => void {
    return this.addCallback(this.onLoadCallbacks, callback);
  }

  public onBlock(callback: () => void): () => void {
    return this.addCallback(this.onBlockCallbacks, callback);
  }

  public onError(callback: (error: Error) => void): () => void {
    return this.addCallback(this.onErrorCallbacks, callback);
  }

  public then(callback: () => void): () => void {
    this.onLoadCallbacks.add(callback);
    this.onBlockCallbacks.add(callback);
    return () => {
      this.onLoadCallbacks.delete(callback);
      this.onBlockCallbacks.delete(callback);
    };
  }

  private addCallback<C>(set: Set<C>, callback: C): () => void {
    set.add(callback);
    return () => {
      set.delete(callback);
    };
  }
}
